import { useState, useEffect, useRef } from "react";
import scraperApi from "api";

const POLL_INTERVAL = 10_000;

interface UserCounters {
  requestCount: number;
  concurrentRequests: number;
  totalCredits: number;
  concurrencyLimit: number;
  payAsYouGoCredits: number;
}

type UseUserCountersReturn = {
  counters: UserCounters;
  isLoading: boolean;
  error: null;
} | {
  counters: null;
  isLoading: boolean;
  error: string;
}

/*
 * Note: This periodically updates the values
 */
const useUserCounters = (interval = POLL_INTERVAL): UseUserCountersReturn => {
  const [counters, setCounters] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchCounters = async () => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const data = await scraperApi.stats.counters({ signal: controller.signal });
        if (!controller.signal.aborted) {
          setCounters(data);
          setError(null);
        }
      } catch (err: any) {
        if (err.name === "AbortError" || controller.signal.aborted) {
          return;
        };
        setCounters(null)
        setError(err.message || "Failed to fetch counters");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchCounters();
    const timerId = setInterval(fetchCounters, interval);

    return () => {
      clearInterval(timerId);
      abortControllerRef.current?.abort();
    };
  }, [interval]);

  return { counters, isLoading, error };
};

export default useUserCounters;
